import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Star } from "lucide-react";

// Public page — NO auth. Uses REACT_APP_BACKEND_URL directly (not shared `api` instance).
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicSurveyPage() {
  const { token } = useParams();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BASE}/api/public/surveys/${token}`)
      .then((r) => { setSurvey(r.data); if (r.data.already_responded) setSubmitted(true); })
      .catch((e) => setErr(e.response?.data?.detail || "Inquérito indisponível"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    setErr("");
    try {
      await axios.post(`${BASE}/api/public/surveys/${token}`, { answers });
      setSubmitted(true);
    } catch (e) { setErr(e.response?.data?.detail || "Falha ao enviar resposta"); }
  };

  if (loading) return <PageWrap><div className="text-center text-[#6C757D]">A carregar…</div></PageWrap>;
  if (err && !survey) return <PageWrap><div className="text-center"><div className="font-heading text-lg text-[#DC3545] font-bold">Oops</div><p className="text-sm text-[#6C757D] mt-2">{err}</p></div></PageWrap>;
  if (submitted) return (
    <PageWrap>
      <div className="text-center py-8" data-testid="survey-thanks">
        <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
        <h2 className="font-heading text-2xl font-bold text-[#1A1A2E]">Obrigado!</h2>
        <p className="text-sm text-[#6C757D] mt-2 max-w-sm mx-auto">A sua opinião ajuda-nos a melhorar o serviço. Pode fechar esta página.</p>
      </div>
    </PageWrap>
  );

  const scale = survey.scale || "1-5";
  const scaleMax = scale === "0-10" ? 10 : 5;
  const scaleMin = scale === "0-10" ? 0 : 1;
  const canSubmit = (survey.questions || []).filter((q) => q.type === "score").every((q) => answers[q.id] != null);

  return (
    <PageWrap>
      <div className="text-xs uppercase tracking-widest text-[#1F3864] font-bold mb-2">{survey.survey_type} · {survey.department}</div>
      <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] mb-2" data-testid="survey-subject">{survey.subject_pt}</h1>
      {survey.customer_name && <p className="text-sm text-[#6C757D] mb-4">Olá <b className="text-[#1A1A2E]">{survey.customer_name}</b></p>}
      {survey.intro_pt && <p className="text-sm text-[#6C757D] mb-6">{survey.intro_pt}</p>}

      <div className="space-y-6">
        {(survey.questions || []).map((q) => (
          <div key={q.id} data-testid={`survey-q-${q.id}`}>
            <div className="text-sm font-semibold text-[#1A1A2E] mb-2">{q.text_pt}</div>
            {q.type === "score" ? (
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => scaleMin + i).map((n) => (
                  <button
                    key={n}
                    onClick={() => setAnswers((a) => ({ ...a, [q.id]: n }))}
                    className={`w-10 h-10 rounded-sm border font-mono font-semibold transition ${answers[q.id] === n ? "bg-[#1F3864] text-white border-[#1F3864]" : "bg-white border-gray-300 text-[#1A1A2E] hover:border-[#1F3864]"}`}
                    data-testid={`survey-score-${q.id}-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            ) : (
              <textarea
                rows={3}
                value={answers[q.id] || ""}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F3864]"
                data-testid={`survey-text-${q.id}`}
                placeholder="A sua resposta…"
              />
            )}
          </div>
        ))}
      </div>

      {err && <div className="mt-4 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">{err}</div>}

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="mt-6 w-full bg-[#1F3864] hover:bg-[#142542] text-white font-semibold py-3 rounded-sm disabled:opacity-50"
        data-testid="survey-submit"
      >
        Enviar resposta
      </button>
    </PageWrap>
  );
}

function PageWrap({ children }) {
  return (
    <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-5 border-b border-gray-200 bg-[#1F3864] text-white rounded-t-lg">
          <div className="font-heading text-lg font-bold flex items-center gap-2"><Star className="w-5 h-5" /> Ronil Command Center</div>
        </div>
        <div className="p-6 sm:p-8">{children}</div>
      </div>
    </div>
  );
}
